import React, { useMemo, useState } from 'react';

import Button from '@uikit/Form/Button/Button';
import BillingFormField from '@components/Publisher/Settings/SettingsBillingDetails/_components/BillingFormField/BillingFormField';
import {
  isValidEmailField,
  PAYOUT_MAX,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/payout-validation';
import { PayoutFieldErrorType } from '@ts/billing-publisher.types';

import './WiseAccountForm.scss';

export type WiseAccountFormValue = {
  email: string;
  accountHolderName: string;
};

type Props = {
  initialValue?: Partial<WiseAccountFormValue>;
  onSubmit: (value: WiseAccountFormValue) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
  apiError?: string;
  apiFieldErrors?: PayoutFieldErrorType[];
};

// Backend reports per-field errors using the snake_case proto names.
const API_FIELD_EMAIL = 'email';
const API_FIELD_ACCOUNT_HOLDER_NAME = 'account_holder_name';

function WiseAccountForm({
  initialValue,
  onSubmit,
  onCancel,
  isSubmitting,
  isEditing,
  apiError,
  apiFieldErrors,
}: Props): JSX.Element {
  const [email, setEmail] = useState<string>(initialValue?.email ?? '');
  const [accountHolderName, setAccountHolderName] = useState<string>(
    initialValue?.accountHolderName ?? ''
  );
  const [showErrors, setShowErrors] = useState(false);
  const [dismissedApiFieldKeys, setDismissedApiFieldKeys] = useState<Set<string>>(new Set());

  const apiErrorByField = useMemo(() => {
    const map: Record<string, string> = {};
    apiFieldErrors?.forEach(({ field, message }) => {
      if (field && message) map[field] = message;
    });
    return map;
  }, [apiFieldErrors]);

  const apiErrorFor = (apiKey: string): string | undefined =>
    dismissedApiFieldKeys.has(apiKey) ? undefined : apiErrorByField[apiKey];

  const dismissApiFieldError = (apiKey: string) => {
    setDismissedApiFieldKeys((prev) => {
      if (prev.has(apiKey)) return prev;
      const next = new Set(prev);
      next.add(apiKey);
      return next;
    });
  };

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!isValidEmailField(email)) {
      errors.email = 'Enter the email of your Wise account (max 200 characters).';
    }
    if (!accountHolderName.trim()) {
      errors.accountHolderName = 'Enter the full name on the Wise account.';
    } else if (accountHolderName.trim().length > PAYOUT_MAX.contactName) {
      errors.accountHolderName = `Name must be ${PAYOUT_MAX.contactName} characters or fewer.`;
    }
    return errors;
  }, [email, accountHolderName]);

  const errorFor = (
    apiKey: string,
    validationKey: keyof typeof fieldErrors
  ): string | undefined => {
    const apiErr = apiErrorFor(apiKey);
    if (apiErr) return apiErr;
    return showErrors ? fieldErrors[validationKey] : undefined;
  };

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleEmailChange = (next: string) => {
    setEmail(next);
    if (apiErrorByField[API_FIELD_EMAIL]) dismissApiFieldError(API_FIELD_EMAIL);
  };

  const handleHolderChange = (next: string) => {
    setAccountHolderName(next);
    if (apiErrorByField[API_FIELD_ACCOUNT_HOLDER_NAME]) {
      dismissApiFieldError(API_FIELD_ACCOUNT_HOLDER_NAME);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!isFormValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    await onSubmit({
      email: email.trim(),
      accountHolderName: accountHolderName.trim(),
    });
  };

  return (
    <form
      className="wise-account-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      noValidate
    >
      <p className="wise-account-form__description">
        Pay out instantly to a Wise account with zero fees. Enter the email of the Wise account
        that will receive your USD payouts.
      </p>

      <BillingFormField
        label="Wise account email"
        placeholder="name@example.com"
        value={email}
        onChange={handleEmailChange}
        isError={!!errorFor(API_FIELD_EMAIL, 'email')}
        errorMessage={errorFor(API_FIELD_EMAIL, 'email')}
        maxLength={PAYOUT_MAX.email}
      />

      <BillingFormField
        label="Full name on the Wise account"
        placeholder="As it appears on the Wise account"
        value={accountHolderName}
        onChange={handleHolderChange}
        isError={!!errorFor(API_FIELD_ACCOUNT_HOLDER_NAME, 'accountHolderName')}
        errorMessage={errorFor(API_FIELD_ACCOUNT_HOLDER_NAME, 'accountHolderName')}
        maxLength={PAYOUT_MAX.contactName}
      />

      {!!apiError && (
        <div className="wise-account-form__api-error" role="alert">
          {apiError}
        </div>
      )}

      <div className="wise-account-form__footer">
        <Button
          className="wise-account-form__submit"
          size="l"
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isEditing ? 'Update Wise account' : 'Connect Wise account'}
        </Button>
        <Button type="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default WiseAccountForm;
